import { Box, Link, Typography } from "@mui/material";
import { useCerberus } from "../../hooks/useCerberus";
import SessionService from "../../services/SessionService";
import { useBranding } from "../../branding/BrandingProvider";
import AuthPageFrame, { AUTH_LINK_COLOR } from "./AuthPageFrame";
import PhoneMfaSetup from "./PhoneMfaSetup";

/**
 * Blocking post-login step: staff, providers and external providers must verify a mobile number
 * before they can use the app, so their sign-in codes can be delivered by SMS. Rendered by App.tsx
 * (in place of the app shell) whenever the identity flag `mfaSetupRequired` is true. There is no
 * skip — the only ways out are verifying a phone or signing out. Verifying refreshes the identity,
 * which clears the flag and lets the app render normally.
 *
 * Framed like the sign-in page (AuthPageFrame), because to the user it is the last step of signing
 * in rather than a page of the app.
 */
export default function MfaSetupScreen() {
  const brand = useBranding();
  const { refreshUserData } = useCerberus();

  const handleSignOut = async () => {
    await SessionService.getInstance().logout();
    window.location.href = "/";
  };

  return (
    <AuthPageFrame
      eyebrow="One more step"
      title="Secure your account"
      titleGap={2}
    >
      <Typography
        variant="body2"
        sx={{ mb: 3, textAlign: "center", color: "text.secondary" }}
      >
        To keep {brand.displayName} secure, verify a mobile phone number. We'll
        text a six-digit code to it each time you sign in on a new device. This
        is required for all staff and provider accounts.
      </Typography>

      <PhoneMfaSetup onVerified={refreshUserData} hideVerifiedChip stacked />

      <Box sx={{ mt: 3, textAlign: "center" }}>
        {/* type="button": a bare <button> defaults to submit (see Login's resend link). */}
        <Link
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          onClick={handleSignOut}
          sx={{ color: AUTH_LINK_COLOR, fontWeight: 500 }}
        >
          Sign out
        </Link>
      </Box>
    </AuthPageFrame>
  );
}
